import React from 'react';

import { font, palette } from './styles';
import { usePortalCompact } from './usePortalCompact';
import { onboardColors } from '../theme/onboardTokens';

import type { IPortalItemRow } from './types';

// ─── constants ──────────────────────────────────────────────────────────────

export const FADE_STYLE = `
@keyframes _portalTabFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.portal-row { transition: background 0.12s ease; }
.portal-row--interactive:hover { background: ${onboardColors.surface.subtle}; }
`;

export type TDocTab = 'all' | 'need_action' | 'completed';
export type TTodoTab = 'all' | 'todo' | 'completed';

export const DOC_TABS: { key: TDocTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'need_action', label: 'Need action' },
  { key: 'completed', label: 'Completed' }
];

export const TODO_TABS: { key: TTodoTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'todo', label: 'To do' },
  { key: 'completed', label: 'Completed' }
];

export const CONTRACT_TABS: { key: TDocTab; label: string }[] = [
  { key: 'need_action', label: 'Need action' },
  { key: 'completed', label: 'Completed' }
];

export const TYPE_LABEL: Record<string, string> = {
  document: 'Document',
  library_document: 'Document',
  smart_form: 'Form',
  contract_signature: 'Contract',
  todo: 'To-do'
};

export const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`;
  } catch {
    return iso;
  }
};

// ─── styles ─────────────────────────────────────────────────────────────────

export const HEADER_BASE: React.CSSProperties = {
  display: 'grid',
  columnGap: 16,
  paddingBottom: 12,
  borderBottom: `1px solid ${palette.divider}`,
  fontSize: 13,
  color: palette.primary,
  fontWeight: 700
};

export const ROW_BASE: React.CSSProperties = {
  display: 'grid',
  alignItems: 'center',
  columnGap: 16,
  padding: '18px 0',
  borderBottom: `1px solid ${palette.divider}`
};

// Stacked-card variant of a row, used below the compact breakpoint instead of the grid.
const ROW_COMPACT: React.CSSProperties = {
  padding: '16px 0',
  borderBottom: `1px solid ${palette.divider}`
};

const ROW_LABEL: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
  color: palette.bodyNeutral
};

export const DOC_COLS = '2fr 1fr 1.5fr 1fr 130px';
export const TODO_COLS = '2.5fr 1.5fr 1fr 130px';
export const CONTRACT_COLS = '2fr 1fr 1.5fr 130px';

// ─── small presentational components ────────────────────────────────────────

export const InfoIcon = (): React.ReactElement => (
  <svg
    fill="none"
    height="18"
    style={{ flexShrink: 0, marginTop: 1 }}
    viewBox="0 0 18 18"
    width="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="9" cy="9" fill={palette.teal} r="9" />
    <text
      dominantBaseline="middle"
      fill={onboardColors.surface.white}
      fontSize="12"
      fontWeight="700"
      textAnchor="middle"
      x="9"
      y="9.5"
    >
      i
    </text>
  </svg>
);

export const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      background: onboardColors.surface.white,
      border: `1px solid ${palette.teal}`,
      borderRadius: 16,
      padding: '14px 16px',
      marginBottom: 20,
      color: palette.bodyNeutral,
      fontSize: 14,
      fontFamily: font
    }}
  >
    <InfoIcon />
    <span>{children}</span>
  </div>
);

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

export const FilterChip: React.FC<ChipProps> = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    style={{
      padding: '4px 14px',
      height: 28,
      borderRadius: 16,
      border: 'none',
      background: active ? palette.primaryTint : palette.pillNeutral,
      color: active ? palette.primary : palette.black,
      fontFamily: font,
      fontSize: 16,
      fontWeight: active ? 600 : 400,
      cursor: 'pointer',
      transition: 'background 0.18s ease, color 0.18s ease'
    }}
  >
    {label}
  </button>
);

export const SectionCard: React.FC<{ children: React.ReactNode; last?: boolean }> = ({
  children,
  last
}) => {
  const compact = usePortalCompact();

  return (
    <div
      style={{
        background: onboardColors.surface.white,
        borderRadius: compact ? 20 : 32,
        padding: compact ? '24px 16px' : '32px 40px',
        marginBottom: last ? 0 : 24
      }}
    >
      {children}
    </div>
  );
};

export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2
    style={{
      fontSize: 24,
      fontWeight: 700,
      color: palette.headingNeutral,
      margin: '0 0 20px',
      fontFamily: font,
      lineHeight: '30px'
    }}
  >
    {children}
  </h2>
);

export const RequiredAsterisk = (): React.ReactElement => (
  <span aria-hidden="true" style={{ color: palette.mediumRed, marginLeft: 2 }}>
    *
  </span>
);

export const StepLabel: React.FC<{ row: IPortalItemRow }> = ({ row }) => (
  <span>
    {row.label}
    {row.required ? <RequiredAsterisk /> : null}
  </span>
);

export const EmptyRow = (): React.ReactElement => (
  <div style={{ padding: '40px 0', textAlign: 'center', color: palette.bodyNeutral, fontSize: 14 }}>
    No items in this view.
  </div>
);

export const TodoToggleIcon: React.FC<{ completed: boolean }> = ({ completed }) => (
  <svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <circle
      cx="12"
      cy="12"
      fill={completed ? onboardColors.status.successPortal : 'none'}
      r="9.25"
      strokeWidth="2.25"
      stroke={
        completed ? onboardColors.status.successPortal : onboardColors.neutral.progressInactive
      }
    />
    <path
      d="M7.7 12.1L10.5 14.8L16.2 9.3"
      stroke={completed ? onboardColors.surface.white : onboardColors.neutral.progressInactive}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.25"
    />
  </svg>
);

export const FadeContent: React.FC<{ tabKey: string; children: React.ReactNode }> = ({
  tabKey,
  children
}) => (
  <div key={tabKey} style={{ animation: '_portalTabFadeIn 0.2s ease both' }}>
    {children}
  </div>
);

interface PortalSectionProps<TTab extends string> {
  title: string;
  info: React.ReactNode;
  tabs: readonly { key: TTab; label: string }[];
  activeTab: TTab;
  onTabChange: (tab: TTab) => void;
  cols: string;
  headers: string[];
  last?: boolean;
  children: React.ReactNode;
}

export const PortalSection = <TTab extends string>({
  title,
  info,
  tabs,
  activeTab,
  onTabChange,
  cols,
  headers,
  last,
  children
}: PortalSectionProps<TTab>): React.ReactElement => {
  const compact = usePortalCompact();

  return (
    <SectionCard last={last}>
      <SectionTitle>{title}</SectionTitle>
      {info}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: compact ? 8 : 24 }}>
        {tabs.map((t) => (
          <FilterChip
            key={t.key}
            active={activeTab === t.key}
            label={t.label}
            onClick={() => onTabChange(t.key)}
          />
        ))}
      </div>
      {/* Stacked rows label each value themselves, so the column header is desktop-only. */}
      {compact ? null : (
        <div style={{ ...HEADER_BASE, gridTemplateColumns: cols }}>
          {headers.map((h, i) => (
            <div key={h} style={i === 0 ? { paddingLeft: 16 } : undefined}>
              {h}
            </div>
          ))}
          <div />
        </div>
      )}
      <FadeContent tabKey={activeTab}>{children}</FadeContent>
    </SectionCard>
  );
};

export interface IPortalRowCell {
  header: string;
  content: React.ReactNode;
  style?: React.CSSProperties;
}

interface PortalRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  cols: string;
  label: React.ReactNode;
  cells: IPortalRowCell[];
  actions?: React.ReactNode;
}

/**
 * One item in a PortalSection: a grid row matching the section's column header on
 * desktop, and a stacked card (label + actions, then each cell under its header)
 * below the compact breakpoint. Extra props (role, handlers) go on the row element.
 */
export const PortalRow: React.FC<PortalRowProps> = ({
  cols,
  label,
  cells,
  actions,
  style,
  ...rowProps
}) => {
  const compact = usePortalCompact();

  const actionsCell = (
    <div
      onClick={(e) => e.stopPropagation()}
      role="presentation"
      style={{ flexShrink: 0, paddingRight: compact ? 0 : 12, textAlign: 'right' }}
    >
      {actions}
    </div>
  );

  if (compact) {
    return (
      <div {...rowProps} style={{ ...ROW_COMPACT, ...style }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <div style={{ ...ROW_LABEL, minWidth: 0, overflowWrap: 'anywhere', paddingTop: 4 }}>
            {label}
          </div>
          {actionsCell}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            alignItems: 'center',
            columnGap: 16,
            rowGap: 8,
            marginTop: 12
          }}
        >
          {cells.map((cell) => (
            <React.Fragment key={cell.header}>
              <div style={{ color: palette.primary, fontSize: 13, fontWeight: 700 }}>
                {cell.header}
              </div>
              <div style={cell.style}>{cell.content}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div {...rowProps} style={{ ...ROW_BASE, gridTemplateColumns: cols, ...style }}>
      <div style={{ ...ROW_LABEL, paddingLeft: 16 }}>{label}</div>
      {cells.map((cell) => (
        <div key={cell.header} style={cell.style}>
          {cell.content}
        </div>
      ))}
      {actionsCell}
    </div>
  );
};
